import { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../../../lib/dateLocale';
import { SttTopControls } from '../../../sporttia/SttTopControls';
import { SttTable } from '../../../sporttia/all';
import { useCrud } from '../../../lib/hooks';
import ModalPeriod, { Period as ModalPeriodType } from './ModalPeriod';
import { PERMISSIONS } from '../../../lib/permissions';

type PeriodsData = {
    count: number;
    rows: ModalPeriodType[];
};

type Event = {
    id: string | number;
    name: string;
    ini: string;
    end: string;
};

type Props = {
    event: Event;
};

export default function EventPeriods({ event }: Props): React.ReactElement {
    const { t } = useTranslation();
    const [periods, setPeriods, , Get, Post, Put, Delete] =
        useCrud<PeriodsData>();
    const [period, setPeriod] = useState<ModalPeriodType | undefined>();

    function loadPeriods(
        params: { page?: number; rows?: number } = { page: 1, rows: 20 },
    ): void {
        Get(`/events/${event.id}/periods`, { ...params }).then(
            (response: unknown) => {
                const data = response as PeriodsData;
                if (data.rows) {
                    setPeriods(data);
                }
            },
        );
    }

    function savePeriod(period: ModalPeriodType): void {
        if (!period) return;

        if (period.id) {
            const { name, notes, idFacility, status } = period;
            Put(`/events/periods/${period.id}`, {
                name,
                notes,
                idFacility,
                status,
            })
                .then(() => {
                    setPeriod(undefined);
                    loadPeriods();
                })
                .catch(() => {});
        } else {
            const { name, notes, idFacility, status, ini, end, weekdays } =
                period;
            Post(`/events/${event.id}/periods`, {
                name,
                notes,
                idFacility,
                status,
                ini,
                end,
                weekdays,
            })
                .then(() => {
                    setPeriod(undefined);
                    loadPeriods();
                })
                .catch(() => {});
        }
    }

    function deletePeriod(periodToDelete: ModalPeriodType): void {
        Delete(`/events/periods/${periodToDelete.id}`)
            .then(() => {
                setPeriod(undefined);
                loadPeriods();
            })
            .catch(() => {});
    }

    return (
        <>
            <SttTopControls
                mainAction={{
                    type: 'create',
                    permission: PERMISSIONS.EVENT.MODIFY,
                    caption: t('Create'),
                    onClick: () => {
                        setPeriod({
                            name: event.name,
                            ini: event.ini,
                            end: event.end,
                            weekdays: [],
                            status: 'ACTIVE',
                        });
                    },
                }}
                pl={0}
            />
            <SttTable
                data={periods}
                onFetch={loadPeriods}
                columns={[
                    {
                        title: t('Name'),
                        type: 'link',
                        field: 'name',
                        align: 'left',
                        onClick: (row: ModalPeriodType) => setPeriod(row),
                    },
                    {
                        title: t('Facility'),
                        type: 'facility',
                        field: 'facility',
                    },
                    {
                        title: t('Day'),
                        type: 'text',
                        value: (row: ModalPeriodType) =>
                            format(parseISO(row.ini), 'EEEE', {
                                locale: getDateLocale(),
                            }),
                    },
                    {
                        title: t('Date'),
                        type: 'dmy',
                        field: 'ini',
                    },
                    {
                        title: t('Time'),
                        type: 'hmRange',
                        value: (row: ModalPeriodType) => ({
                            ini: row.ini,
                            end: row.end,
                        }),
                    },
                ]}
            />
            {period && (
                <ModalPeriod
                    item={period as any}
                    event={event}
                    onClose={() => setPeriod(undefined)}
                    onSave={savePeriod}
                    onDelete={deletePeriod as any}
                />
            )}
        </>
    );
}
